// CampaignTypeSelector.tsx

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { Download, Search, Send } from "@mui/icons-material";
import type { CobranzasCampaignType } from "../../types";

export const cobranzasCampaignLabels: Record<CobranzasCampaignType, string> = {
  PREVENTIVA: "Campaña Preventiva",
  RECORDATORIO: "Recordatorio",
  CORTE: "Corte",
};

interface CampaignTypeSelectorProps {
  value: CobranzasCampaignType | "";
  onChange: (value: CobranzasCampaignType) => void;
  onSearch: () => void;
  onSend: () => void;
  onDownload: () => void;
  canDownload: boolean;
  loading: boolean;
  sending: boolean;
  soloConValor: boolean;
  onSoloConValorChange: (value: boolean) => void;
}

export const CampaignTypeSelector = ({
  value,
  onChange,
  onSearch,
  onSend,
  onDownload,
  canDownload,
  loading,
  sending,
  soloConValor,
  onSoloConValorChange,
}: CampaignTypeSelectorProps) => {
  const selectId = "cobranzas-campaign-select";

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ width: "100%", justifyContent: "flex-start" }}
    >
      <FormControl
        size="small"
        sx={{ minWidth: { xs: "100%", sm: 260 }, maxWidth: { sm: 320 } }}
      >
        <InputLabel id={`${selectId}-label`}>Tipo de campaña</InputLabel>
        <Select
          labelId={`${selectId}-label`}
          id={selectId}
          value={value}
          label="Tipo de campaña"
          disabled={loading || sending}
          onChange={(event) =>
            onChange(event.target.value as CobranzasCampaignType)
          }
        >
          <MenuItem value="">Seleccionar</MenuItem>
          {(
            Object.entries(cobranzasCampaignLabels) as [
              CobranzasCampaignType,
              string,
            ][]
          ).map(([type, label]) => (
            <MenuItem key={type} value={type}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title="Excluye los registros con valor 0 del resultado." arrow>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={soloConValor}
              disabled={loading}
              onChange={(event) => onSoloConValorChange(event.target.checked)}
            />
          }
          label="Solo con valores generados"
          sx={{ whiteSpace: "nowrap" }}
        />
      </Tooltip>

      <Button
        variant="contained"
        color="primary"
        size="medium"
        startIcon={<Search />}
        disabled={!value || loading}
        onClick={onSearch}
        sx={{
          flex: { xs: 1, sm: "0 1 auto" },
          minWidth: { xs: "auto", sm: 140 },
          textTransform: "none",
          fontWeight: 600,
          boxShadow: 2,
          "&:hover": { boxShadow: 4 },
        }}
      >
        Consultar
      </Button>

      <Button
        variant="contained"
        color="secondary"
        size="medium"
        startIcon={<Send />}
        disabled={!value || sending}
        onClick={onSend}
        sx={{
          flex: { xs: 1, sm: "0 1 auto" },
          minWidth: { xs: "auto", sm: 170 },
          textTransform: "none",
          fontWeight: 600,
          boxShadow: 2,
          "&:hover": { boxShadow: 4 },
        }}
      >
        Enviar a Genesys
      </Button>

      <Button
        variant="outlined"
        color="primary"
        size="medium"
        startIcon={<Download />}
        disabled={!canDownload || loading || sending}
        onClick={onDownload}
        sx={{
          flex: { xs: 1, sm: "0 1 auto" },
          minWidth: { xs: "auto", sm: 160 },
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        Descargar Excel
      </Button>
    </Stack>
  );
};

export default CampaignTypeSelector;
